// src/utils/themeUtils.js
//
// Shared theme resolution.
//
// The preference is THREE values, not one:
//   themeMode   'auto' | 'light' | 'dark'    - auto follows the OS
//   lightTheme  a light theme id             - used whenever we resolve to light
//   darkTheme   a dark theme id              - used whenever we resolve to dark
//
// That's what makes Auto a switch rather than a theme: it picks between two
// standing choices instead of being a choice itself. With Auto off, themeMode
// records which side the user last picked.
//
// Sets TWO attributes on <html>:
//   data-theme  the specific theme, for rules unique to one theme
//   data-mode   which shared rule set the theme paints under
//
// IMPORTANT -- there are two different notions of "side" here, and conflating
// them breaks Classic:
//
//   getThemeSide()  the data-mode value. Classic returns 'classic', NOT 'dark'.
//   getAutoSide()   light-or-dark for the Auto switch. Classic returns 'dark'.
//
// Why: the app's stylesheet was originally written with Classic as the default
// look, so Classic needs almost no overrides of its own. The [data-mode="dark"]
// rules are comfort-dark's overrides, which came later. If Classic painted
// under data-mode="dark" it would inherit all of them and stop looking like
// Classic. So it paints under its own mode, while still counting as the dark
// choice when Auto resolves.

export const MODES = { LIGHT: 'light', DARK: 'dark' };

export const THEME_MODES = {
  AUTO: 'auto',
  LIGHT: MODES.LIGHT,
  DARK: MODES.DARK
};

// The data-mode value each theme paints under. A new theme that's happy to
// inherit the shared rules for its side uses MODES.LIGHT or MODES.DARK; one
// that needs to opt out (like Classic) gets its own string.
export const THEME_SIDE = {
  'classic-light': MODES.LIGHT,  // sky blue, matches govglance.org
  'comfort-light': MODES.LIGHT,  // warm cream
  'comfort-dark': MODES.DARK,    // soft neutral dark
  classic: 'classic'             // original deep blue - opts out, see above
};

// Which themes count as light for the Auto switch. Everything not listed here
// is treated as dark.
const LIGHT_THEME_IDS = ['classic-light', 'comfort-light'];

export const VALID_THEME_IDS = Object.keys(THEME_SIDE);

export const DEFAULT_LIGHT_THEME = 'classic-light';
export const DEFAULT_DARK_THEME = 'comfort-dark';

// Retired ids mapped onto their current equivalent.
const LEGACY_THEME_IDS = {
  light: 'comfort-light',
  dark: 'comfort-dark',
  daylight: 'comfort-light'
};

export const STORAGE_KEYS = {
  MODE: 'themeMode',
  LIGHT: 'lightTheme',
  DARK: 'darkTheme',
  LEGACY: 'theme'
};

const read = (key) => {
  try { return localStorage.getItem(key); } catch (e) { return null; }
};

const write = (key, value) => {
  try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
};

// The data-mode attribute value. Used only when painting.
export const getThemeSide = (themeId) => THEME_SIDE[themeId] || MODES.LIGHT;

// Light-or-dark for the Auto switch and for storage. Used everywhere else.
export const getAutoSide = (themeId) =>
  LIGHT_THEME_IDS.includes(themeId) ? MODES.LIGHT : MODES.DARK;

export const prefersDarkScheme = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Converts the old single 'theme' key into the three-key shape. Runs once per
// install; after that the new keys exist and this is a no-op.
const migrateLegacy = () => {
  if (read(STORAGE_KEYS.MODE)) return;

  const legacy = read(STORAGE_KEYS.LEGACY);
  if (!legacy) return;

  if (legacy === THEME_MODES.AUTO) {
    write(STORAGE_KEYS.MODE, THEME_MODES.AUTO);
    return;
  }

  const themeId = VALID_THEME_IDS.includes(legacy) ? legacy : LEGACY_THEME_IDS[legacy];
  if (!themeId) return;

  const side = getAutoSide(themeId);
  write(STORAGE_KEYS.MODE, side);
  write(side === MODES.DARK ? STORAGE_KEYS.DARK : STORAGE_KEYS.LIGHT, themeId);
};

const coerce = (value, side) => {
  if (VALID_THEME_IDS.includes(value) && getAutoSide(value) === side) return value;
  return side === MODES.DARK ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
};

export const getThemePreferences = () => {
  migrateLegacy();

  const storedMode = read(STORAGE_KEYS.MODE);
  const mode = Object.values(THEME_MODES).includes(storedMode) ? storedMode : THEME_MODES.AUTO;

  return {
    mode,
    isAuto: mode === THEME_MODES.AUTO,
    lightTheme: coerce(read(STORAGE_KEYS.LIGHT), MODES.LIGHT),
    darkTheme: coerce(read(STORAGE_KEYS.DARK), MODES.DARK)
  };
};

// The one place that decides which concrete theme is showing.
export const resolveTheme = (prefs = getThemePreferences()) => {
  if (prefs.mode === THEME_MODES.DARK) return prefs.darkTheme;
  if (prefs.mode === THEME_MODES.LIGHT) return prefs.lightTheme;
  return prefersDarkScheme() ? prefs.darkTheme : prefs.lightTheme;
};

export const applyTheme = (prefs = getThemePreferences()) => {
  const themeId = resolveTheme(prefs);
  document.documentElement.setAttribute('data-theme', themeId);
  document.documentElement.setAttribute('data-mode', getThemeSide(themeId));
  return themeId;
};

export const applyStoredTheme = () => applyTheme();

const notify = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('storage'));
};

// ── Mutations ───────────────────────────────────────────────────────────────

// Auto switch. Turning it OFF keeps whatever is showing, so the screen doesn't
// flip under the user at the moment they flick it.
export const setAutoEnabled = (enabled) => {
  if (enabled) {
    write(STORAGE_KEYS.MODE, THEME_MODES.AUTO);
  } else {
    write(STORAGE_KEYS.MODE, prefersDarkScheme() ? THEME_MODES.DARK : THEME_MODES.LIGHT);
  }
  applyTheme();
  notify();
  return getThemePreferences();
};

// Picking a theme records it as that side's preference and makes it active.
// (The picker is disabled while Auto is on, so this only runs in manual mode --
// but it stays correct either way.)
export const selectTheme = (themeId) => {
  if (!VALID_THEME_IDS.includes(themeId)) return getThemePreferences();

  const side = getAutoSide(themeId);
  write(side === MODES.DARK ? STORAGE_KEYS.DARK : STORAGE_KEYS.LIGHT, themeId);

  if (read(STORAGE_KEYS.MODE) !== THEME_MODES.AUTO) {
    write(STORAGE_KEYS.MODE, side);
  }

  applyTheme();
  notify();
  return getThemePreferences();
};

export const watchSystemTheme = (onChange) => {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};

  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handle = () => {
    if (getThemePreferences().mode !== THEME_MODES.AUTO) return;
    const themeId = applyTheme();
    if (onChange) onChange(themeId);
  };

  if (mq.addEventListener) {
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }
  mq.addListener(handle);
  return () => mq.removeListener(handle);
};